import { serverClient } from '@obvio/server'

import { schema } from '@/api/schema'

import type { AnyObject } from '@obvio/utils'
import type {
  GetStaticPathsResult,
  GetStaticPropsContext,
  GetStaticPropsResult,
} from 'next'

export async function getStaticProps(
  ctx: GetStaticPropsContext<{ slug: string }>,
): Promise<GetStaticPropsResult<AnyObject>> {
  const client = await serverClient(schema, { ...ctx, isIsr: true })

  if (!ctx?.params?.slug) {
    return { notFound: true }
  }

  const product = await client.query('getProduct', {
    args: {
      slug: ctx?.params.slug,
    },
    select: {
      id: true,
      slug: true,
      title: true,
      description: true,
      seoTitle: true,
      seoDescription: true,
      trackInventory: true,
      ageRestriction: true,
      variants: {
        id: true,
        inventory: true,
        price: {
          sale: true,
          price: {
            amount: true,
          },
        },
        regularPrice: {
          sale: true,
          price: {
            amount: true,
          },
        },
        lowestPriceBeforeSale: {
          sale: true,
          price: {
            amount: true,
          },
        },
        attributes: {
          id: true,
          value: true,
          attribute: {
            id: true,
            title: true,
          },
        },
      },
      images: true,
      category: {
        title: true,
        ageRestriction: true,
      },
      attributes: {
        id: true,
        title: true,
        values: {
          id: true,
          value: true,
        },
        static: true,
      },
      price: {
        price: true,
        lowestPriceBeforeSale: true,
        regularPrice: true,
        otherPrices: true,
        currency: true,
      },
    },
  })

  const categories = await client.query('getProductCategories', {
    select: {
      slug: true,
    },
  })

  const randomCategory =
    categories[Math.floor(Math.random() * categories.length)]

  // Needs to be sent via props cause of randomness??
  const recommendedCategory = await client.query('getProductCategory', {
    args: {
      slug: randomCategory.slug,
    },
    select: {
      slug: true,
      title: true,
      description: true,
      products: {
        title: true,
        images: true,
      },
    },
  })

  if (!product) {
    return { notFound: true }
  }

  return {
    ...client.ssrData({ product, recommendedCategory }),
    revalidate: 100,
  }
}

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  const client = await serverClient(schema, { isAdmin: true })
  const products = await client.query('getProducts', {
    select: {
      slug: true,
    },
  })

  return {
    paths: products.map(({ slug }: { slug: string }) => ({ params: { slug } })),
    fallback: 'blocking',
  }
}

export { ProductPage as default } from '@/components/Store/ProductPage'
